import {
  useAgentConversationList,
  WORKFLOW_BUILDER_PROFILE_KEY,
} from '@commenda/api/commenda-ai';
import { AgentMode, IWorkflowTemplateVersion } from '@commenda/types';
import { useCallback, useState } from 'react';
import { BuilderChatContextHelper } from './builder-chat-context.helper';
import { useBuilderVersionPolling } from './hooks/useBuilderVersionPolling';
import {
  TBuilderCanvasBinding,
  TBuilderOpenTab,
  WorkflowVersionTargetOutcome,
} from './types/types';
import { useBuilderCanvasSync } from './useBuilderCanvasSync';
import { useBuilderChatSend } from './useBuilderChatSend';
import { useBuilderChatVersionActions } from './useBuilderChatVersionActions';
import { useBuilderSendBody } from './useBuilderSendBody';
import { useVersionTargeting } from './useVersionTargeting';

export function useBuilderChatPanel(
  params: Readonly<{
    workflowTemplateVersion: IWorkflowTemplateVersion;
    conversationId: string | null;
    canvas: TBuilderCanvasBinding;
    modelId: string;
    onConversationCreated: (conversationId: string) => void;
    onNavigateToVersion: (workflowTemplateVersionId: string) => void;
    onSelectConversation: (conversationId: string) => void;
    /** Which document tab the host has open. Absent means the host has no tab strip, and no screen
     *  context is sent at all: that is what leaves the admin builder untouched. */
    openTab?: TBuilderOpenTab;
  }>
) {
  const {
    workflowTemplateVersion,
    conversationId,
    canvas,
    modelId,
    onConversationCreated,
    onNavigateToVersion,
    onSelectConversation,
    openTab,
  } = params;
  const { workflowTemplateId } = workflowTemplateVersion;

  const [chosenMode, setChosenMode] = useState<AgentMode | null>(null);
  const [forkError, setForkError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // The shared chat screen owns the run now, so "a turn is in flight" is lifted out of it rather than
  // derived here. Version polling, canvas sync and the composer all read it.
  const [isGenerating, setIsGenerating] = useState(false);

  const versionPoll = useBuilderVersionPolling(workflowTemplateVersion.id, {
    isEditInProgress: isGenerating,
  });
  const builderConversations = useAgentConversationList(
    workflowTemplateId
      ? { profile: WORKFLOW_BUILDER_PROFILE_KEY, workflowTemplateId }
      : null
  );

  const liveVersion = versionPoll.version ?? workflowTemplateVersion;

  const targeting = useVersionTargeting({
    version: liveVersion,
    // The binding rides the send payload, so "bind then navigate" reduces to navigating.
    onForked: onNavigateToVersion,
  });

  const versionActions = useBuilderChatVersionActions({
    version: liveVersion,
    onNavigateToVersion,
  });

  // Suppression is per message, cleared after each send: removing the pill answers "not this one",
  // not "stop telling the agent where I am". It never touches the canvas selection or the conversation.
  const [isContextSuppressed, setIsContextSuppressed] = useState(false);

  const buildContext = useCallback(
    () =>
      BuilderChatContextHelper.build({
        openTab,
        selection: canvas.getSelection(),
        version: liveVersion,
        isEditable: targeting.isEditable,
      }),
    [openTab, canvas, liveVersion, targeting.isEditable]
  );

  const sender = useBuilderChatSend({
    workflowTemplateVersionId: liveVersion.id,
    conversationId,
    canvas,
    isEditable: targeting.isEditable,
    resolveTarget: targeting.resolveTarget,
    onConversationCreated,
    onConversationsChanged: builderConversations.mutate,
    onRunStarted: () => setIsGenerating(true),
    getBuilderContext: () => (isContextSuppressed ? null : buildContext()),
  });

  useBuilderCanvasSync({
    canvas,
    polledVersion: versionPoll.version,
    conversationId,
    conversations: builderConversations.conversations,
    isEditInProgress: isGenerating,
    isEditable: targeting.isEditable,
    onSelectConversation,
  });

  const mode =
    chosenMode ?? (targeting.isEditable ? AgentMode.AGENT : AgentMode.PLAN);

  const { beforeSend, buildSendBody } = useBuilderSendBody({
    mode,
    modelId,
    targetWorkflowTemplateVersionId: liveVersion.id,
    isEditable: targeting.isEditable,
    canvas,
    getBuilderContext: () => (isContextSuppressed ? null : buildContext()),
    onContextSent: () => setIsContextSuppressed(false),
    onSaveFailed: setSaveError,
  });

  /** Agent mode writes and chat only ever writes to a draft, so a published version forks the moment
   *  the mode is chosen. The fork used to happen at send time; the shared composer reads its target
   *  synchronously, so it has to have happened by then. */
  const setMode = useCallback(
    async (nextMode: AgentMode) => {
      setChosenMode(nextMode);
      setForkError(null);
      if (nextMode !== AgentMode.AGENT || targeting.isEditable) return;

      const target = await targeting.resolveTarget(nextMode);
      if (target.outcome === WorkflowVersionTargetOutcome.FAILED) {
        setForkError(target.error);
        // No draft to write to, so fall back to the mode that never writes rather than leave the
        // composer pointed at a version the server would refuse.
        setChosenMode(AgentMode.PLAN);
      }
    },
    [targeting]
  );

  return {
    liveVersion,
    targeting,
    versionActions,
    isGenerating,
    onGeneratingChange: setIsGenerating,
    beforeSend,
    buildSendBody,
    mode,
    setMode,
    send: async (
      content: string,
      attachments: Parameters<typeof sender.send>[1],
      options: Parameters<typeof sender.send>[2]
    ) => {
      const sent = await sender.send(content, attachments, options);
      if (sent) setIsContextSuppressed(false);

      return sent;
    },
    contextLabel: isContextSuppressed
      ? null
      : BuilderChatContextHelper.describe(buildContext()),
    onContextRemove: () => setIsContextSuppressed(true),
    inlineError: sender.error ?? forkError ?? saveError ?? versionActions.error,
    conversations: builderConversations.conversations,
    isLoading: builderConversations.isLoading,
    loadError: versionPoll.error ?? builderConversations.error,
  };
}
